import { Drawer } from 'antd'
import Select from 'react-select'
import pluralize from 'pluralize'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import Confirm from '../reusable/confirm'
import { useListData, usePet, useToggle } from '../../modules'

const ConsumablesRequest = ({ patient, storeName, department, onPerform }) => {
    const { id } = useParams()

    const [string, setString] = useState()
    const [stores, setStores] = useState([])
    const [selected, setSelected] = useState([])
    const [destination, setDestination] = useState()

    const { enqueueSnackbar } = useSnackbar()

    const history = useHistory()

    const [visible, toggleVisible] = useToggle(true)

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }
    
    const [
        { loading: invLoading, error: invError, fetch },
        { list: inventory = [] }
    ] = useListData('inventory', {
        store: destination?._id,
        string
    }, { notify: true, manual: true })

    const onComplete = ({ }, notif, ops) => {
        notif('Request has been sent', ops('success'))
        onPerform()
        close()
    }
    
    const [{ loading: rqLoading }, requestConsumables] = usePet('requestProcedureConsumables', {
        source: department,
        destination: destination?._id,
        requests: selected,
        orderItems: [id],
        autoIssue: destination?.name === storeName
    }, { notify: true, callback: onComplete, manual: true, load: { msg: 'Sending request...' } })

    usePet('stores', { limit: 50, order: 1 }, { notify: true, callback: ({ data }) => setStores(data.stores) })

    const search = str => {
        if (str?.trim()?.length > 2 || !str) {
            setString(str)
            fetch()
        }
    }

    const changeQty = (val, key, item) => {
        if (val > item.qty) {
            enqueueSnackbar(`${destination?.name} has only ${item.qty} ${pluralize(item.item.issuedIn, item.qty)} of ${item.label}`)
            return
        }

        let temp = [...selected]
        temp[key].quantity = val

        setSelected(temp)
    }

    const save = () => Confirm({
        title: 'Request Items?',
        message: 'Please confirm before proceeding?',
        okText: 'Proceed',
        onOk: requestConsumables
    })

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button disabled={ rqLoading } onClick={save} className="text-white hisani-btn-orange mr-3 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save Request</button>
                    <button onClick={close} className="text-white bg-gray-500 hover:bg-gray-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Close</button>
                </div>
            }
            width={600}>
            <p className="font-extrabold text-3xl mb-0">{patient}'s Request</p>
            <div className="p-3 mt-4 h-full">
                <div className="w-1/2 mb-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                        Destination
                    </label>
                    <Select
                        loadingMessage={str => `Searching for "${str?.inputValue}"`}
                        value={destination}
                        onChange={ val => {
                            setDestination(val)
                            fetch()
                        } }
                        options={stores ? stores.map(sto => ({ label: sto.name, value: sto._id, ...sto })) : [] }
                        placeholder="Select Destination" />
                </div>
                <div className="w-full mb-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                        Items
                    </label>
                    <Select
                        isMulti
                        isLoading={ invLoading }
                        loadingMessage={str => `Searching for "${str?.inputValue}"`}
                        onInputChange={search}
                        value={selected}
                        onChange={setSelected}
                        options={ inventory.map(rec => { 
                            let qty = rec.variations.reduce((total, variant) => total + variant.total, 0)

                            return { 
                                label: `${rec.item.name} -(${qty} ${pluralize(rec.item.issuedIn, qty)})`, 
                                value: rec.item._id, 
                                item: rec.item._id, 
                                quantity: 0, 
                                isDisabled: qty <= 0,
                                qty,
                                ...rec.item
                            }
                        }) }
                        placeholder="Search for inventory item" />
                </div>
                {
                    selected.length > 0 ?
                        <table className="table-auto w-full">
                            {/* Table header */}
                            <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                <tr>
                                    <th className="p-2 pl-4 text-center">
                                        <div className="font-semibold">No.</div>
                                    </th>
                                    <th className="p-2 pl-4 text-left">
                                        <div className="font-semibold">Item</div>
                                    </th>
                                    <th className="p-2">
                                        <div className="font-semibold text-center">Quantity</div>
                                    </th>
                                </tr>
                            </thead>
                            {/* Table body */}
                            <tbody className="text-sm font-medium divide-y divide-gray-100">
                                {
                                    selected.map((item, key) =>
                                        <tr>
                                            <td className="p-2 pl-4 h-5 text-center">{key + 1}</td>
                                            <td className="p-2 pl-4 h-5">{item?.name}</td>
                                            <td className="p-2 pl-4 h-5 text-center">
                                                <div className="flex justify-center">
                                                    <input
                                                        className="appearance-none block text-center max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                                        id="grid-qty-item"
                                                        value={item.quantity}
                                                        onChange={e => changeQty(e.target.value, key, item)}
                                                        placeholder={`How many ${item.receivedIn.toLowerCase()}?`}
                                                        type="number" />
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                        : <div className="flex items-center justify-center h-28">
                            <h2>You can add items by clicking above</h2>
                        </div>

                }
            </div>
        </Drawer>
    )
}

export default ConsumablesRequest